<script setup lang="ts">
    import { Button } from '@/components/ui/button';
    import { ChevronRight, LoaderCircle } from 'lucide-vue-next';
    import { ref } from 'vue';
    import axios from 'axios';
    import { toast } from 'vue-sonner';
    import { useI18n } from 'vue-i18n';
    import { EconomicAccount } from '@/types';

    interface Props {
        form: any;
    }

    const { t } = useI18n();

    const props = defineProps<Props>();

    const isLoading = ref(false);

    const emit = defineEmits<{
        (e: 'accounts-loaded', accounts: EconomicAccount[]): void;
    }>();

    const loadApiData = async () => {
        isLoading.value = true;
        try {
            const { data } = await axios.post(route('v2.companies.endpoint.load'), {
                api_token: props.form.api_token,
            });
            handleApiSuccess(data.data);
            handleApiAccounts(data.accounts);
        } catch (error: any) {
            console.error('API call failed:', error);
            handleApiError(error);
        } finally {
            isLoading.value = false;
        }
    };

    const handleApiError = (error: any) => {
        const message = error.response.data.message;
        if (message.includes('401 Unauthorized')) {
            toast.error(t('companies.economic_unauthorized'));
        } else {
            toast.error(t('forms.system_error'));
        }
    };

    const handleApiSuccess = (data: any) => {
        const merged = { ...data };

        if (data.economic_data) {
            merged.cvr = data.economic_data.cvr;
            merged.website = data.economic_data.website;
            merged.economic_number = data.economic_data.economic_number;
        }

        for (const key in merged) {
            if (key in props.form && merged[key] !== undefined) {
                props.form[key] = merged[key];
            }
        }
    };

    const handleApiAccounts = (accounts: EconomicAccount[]) => {
        emit(
            'accounts-loaded',
            accounts.map((account) => ({
                ...account,
                id: Number(account.accountNumber),
            })),
        );
    };
</script>

<template>
    <Button
        variant="ghost_primary"
        size="xs"
        @click.prevent="loadApiData"
        :disabled="form.endpoint !== 'economic' || !form.api_token || isLoading"
    >
        {{ $t('forms.load') }}
        <LoaderCircle
            v-if="isLoading"
            class="animate-spin"
        />
        <ChevronRight
            v-else
            class="text-primary"
        />
    </Button>
</template>
